// UnifiedFilterSystem.jsx - Système de filtres unifié Amazon/Airbnb style
// Mobile: plein écran 2 colonnes | Tablet: panel large | Desktop: sidebar fixe

import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ArrowLeft, Search, MapPin, Star, CheckCircle, Truck,
  Wrench, Package, Euro, Clock, SlidersHorizontal, ChevronRight,
  Building2, Zap, Shield, Calendar, Tag, RotateCcw, ShoppingCart,
  Repeat, Heart
} from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════════
// CONFIGURATION DES FILTRES PAR CONTEXTE
// ══════════════════════════════════════════════════════════════════════════════

const FILTER_CONFIGS = {
  artisans: {
    title: 'Filtres Artisans',
    categories: [
      { id: 'metier', label: 'Métier', icon: Wrench },
      { id: 'localisation', label: 'Localisation', icon: MapPin },
      { id: 'rayon', label: 'Rayon', icon: RotateCcw },
      { id: 'note', label: 'Note / Avis', icon: Star },
      { id: 'disponibilite', label: 'Disponibilité', icon: Clock },
      { id: 'certifie', label: 'Certifié', icon: Shield },
    ],
    quickFilters: ['metier', 'localisation', 'certifie', 'disponibilite'],
  },
  prestations: {
    title: 'Filtres Prestations',
    categories: [
      { id: 'type_prestation', label: 'Type de prestation', icon: Wrench },
      { id: 'tarification', label: 'Tarification', icon: Euro },
      { id: 'localisation', label: 'Localisation', icon: MapPin },
      { id: 'rayon', label: 'Rayon', icon: RotateCcw },
      { id: 'urgence', label: 'Urgence', icon: Zap },
      { id: 'note', label: 'Note / Avis', icon: Star },
    ],
    quickFilters: ['type_prestation', 'tarification', 'urgence', 'localisation'],
  },
  // Acheter à un pro - Produits des boutiques Cockpit Pro
  produits_pro: {
    title: 'Filtres Produits Pro',
    categories: [
      { id: 'categorie', label: 'Rayon', icon: Package },
      { id: 'prix', label: 'Prix', icon: Euro },
      { id: 'livraison', label: 'Livraison', icon: Truck },
      { id: 'marque', label: 'Marque', icon: Tag },
      { id: 'etat', label: 'État', icon: CheckCircle },
      { id: 'note', label: 'Avis', icon: Star },
    ],
    quickFilters: ['categorie', 'prix', 'livraison', 'marque'],
  },
  // Entre particuliers - Annonces marketplace (Achat/Location/Échange/Don)
  marketplace: {
    title: 'Filtres Annonces',
    categories: [
      { id: 'transaction', label: 'Type', icon: ShoppingCart },
      { id: 'categorie', label: 'Catégorie', icon: Package },
      { id: 'prix', label: 'Prix', icon: Euro },
      { id: 'livraison', label: 'Livraison', icon: Truck },
      { id: 'note', label: 'Avis', icon: Star },
      { id: 'marque', label: 'Marque', icon: Tag },
      { id: 'etat', label: 'État', icon: CheckCircle },
    ],
    quickFilters: ['transaction', 'categorie', 'prix', 'livraison'],
  },
  particuliers: {
    title: 'Filtres Annonces',
    categories: [
      { id: 'categorie', label: 'Catégorie', icon: Package },
      { id: 'localisation', label: 'Localisation', icon: MapPin },
      { id: 'rayon', label: 'Rayon', icon: RotateCcw },
      { id: 'prix', label: 'Budget', icon: Euro },
      { id: 'etat', label: 'État', icon: CheckCircle },
      { id: 'date', label: 'Date', icon: Calendar },
    ],
    quickFilters: ['localisation', 'rayon', 'prix', 'etat'],
  },
  reseau: {
    title: 'Filtres Réseau',
    categories: [
      { id: 'type_contenu', label: 'Type de contenu', icon: Package },
      { id: 'metier', label: 'Métier', icon: Wrench },
      { id: 'localisation', label: 'Local', icon: MapPin },
      { id: 'certifie', label: 'Certifié', icon: Shield },
      { id: 'date', label: 'Récent', icon: Calendar },
    ],
    quickFilters: ['type_contenu', 'metier', 'localisation', 'certifie'],
  },
  emploi: {
    title: 'Filtres Emploi',
    categories: [
      { id: 'metier', label: 'Métier', icon: Wrench },
      { id: 'localisation', label: 'Localisation', icon: MapPin },
      { id: 'contrat', label: 'Type de contrat', icon: Building2 },
      { id: 'salaire', label: 'Salaire', icon: Euro },
      { id: 'experience', label: 'Expérience', icon: Clock },
    ],
    quickFilters: ['contrat', 'localisation', 'metier'],
  },
};

// ══════════════════════════════════════════════════════════════════════════════
// DONNÉES DES FILTRES
// ══════════════════════════════════════════════════════════════════════════════

const METIERS = [
  { id: 'jardinier', label: 'Jardinier', count: 16 },
  { id: 'pisciniste', label: 'Pisciniste', count: 12 },
  { id: 'electricien', label: 'Électricien', count: 38 },
  { id: 'plombier', label: 'Plombier', count: 45 },
  { id: 'macon', label: 'Maçon', count: 29 },
  { id: 'carreleur', label: 'Carreleur', count: 22 },
  { id: 'peintre', label: 'Peintre', count: 31 },
  { id: 'menuisier', label: 'Menuisier', count: 18 },
  { id: 'couvreur', label: 'Couvreur', count: 15 },
  { id: 'chauffagiste', label: 'Chauffagiste', count: 24 },
  { id: 'paysagiste', label: 'Paysagiste', count: 20 },
  { id: 'climaticien', label: 'Climaticien', count: 14 },
  { id: 'facadier', label: 'Façadier', count: 11 },
  { id: 'serrurier', label: 'Serrurier', count: 9 },
  { id: 'vitrier', label: 'Vitrier', count: 7 },
  { id: 'charpentier', label: 'Charpentier', count: 13 },
  { id: 'plaquiste', label: 'Plaquiste', count: 19 },
  { id: 'terrassier', label: 'Terrassier', count: 8 },
  { id: 'cuisiniste', label: 'Cuisiniste', count: 11 },
  { id: 'architecte', label: 'Architecte', count: 15 },
  { id: 'geometre', label: 'Géomètre', count: 6 },
  { id: 'expert_batiment', label: 'Expert bâtiment', count: 8 },
  { id: 'bureau_etudes', label: 'Bureau d\'études', count: 5 },
  { id: 'diagnostiqueur', label: 'Diagnostiqueur', count: 12 },
  { id: 'maitre_oeuvre', label: 'Maître d\'œuvre', count: 7 },
];

const RAYONS = [
  { id: '5', label: '5 km' },
  { id: '10', label: '10 km' },
  { id: '25', label: '25 km' },
  { id: '50', label: '50 km' },
  { id: '100', label: '100 km' },
  { id: 'all', label: 'France entière' },
];

const NOTES = [
  { id: '5', label: '5 étoiles', stars: 5 },
  { id: '4.5', label: '4.5 et plus', stars: 4.5 },
  { id: '4', label: '4 étoiles et plus', stars: 4 },
  { id: '3', label: '3 étoiles et plus', stars: 3 },
];

const DISPONIBILITES = [
  { id: 'immediat', label: 'Disponible immédiatement', icon: Zap },
  { id: '48h', label: 'Sous 48h' },
  { id: '7j', label: 'Sous 7 jours' },
  { id: 'rdv', label: 'Sur rendez-vous' },
  { id: 'weekend', label: 'Disponible le week-end' },
];

// Types de transaction pour Marketplace
const TRANSACTION_TYPES = [
  { id: 'achat', label: 'Achat', icon: ShoppingCart, color: '#F97316', description: 'Acheter un produit' },
  { id: 'location', label: 'Location', icon: Clock, color: '#3B82F6', description: 'Louer du matériel' },
  { id: 'echange', label: 'Échange', icon: Repeat, color: '#8B5CF6', description: 'Échanger entre pros' },
  { id: 'don', label: 'Don', icon: Heart, color: '#22C55E', description: 'Recevoir gratuitement' },
];

const VILLES_POPULAIRES = [
  'Paris', 'Lyon', 'Marseille', 'Toulouse', 'Bordeaux', 
  'Nantes', 'Nice', 'Strasbourg', 'Montpellier', 'Lille'
];

// Catégories pour Particuliers
const CATEGORIES_PARTICULIERS = [
  { id: 'outillage', label: 'Outillage', count: 24 },
  { id: 'materiaux', label: 'Matériaux', count: 18 },
  { id: 'piscine-jardin', label: 'Piscine & Jardin', count: 15 },
  { id: 'chauffage', label: 'Chauffage & Clim', count: 12 },
  { id: 'electricite', label: 'Électricité', count: 9 },
  { id: 'plomberie', label: 'Plomberie', count: 11 },
  { id: 'vehicules', label: 'Véhicules & Remorques', count: 7 },
  { id: 'mobilier', label: 'Mobilier chantier', count: 5 },
];

// États produit
const ETATS_PRODUIT = [
  { id: 'neuf', label: 'Neuf' },
  { id: 'comme-neuf', label: 'Comme neuf' },
  { id: 'tres-bon', label: 'Très bon état' },
  { id: 'bon', label: 'Bon état' },
  { id: 'correct', label: 'État correct' },
];

// Tranches de prix
const TRANCHES_PRIX = [
  { id: '0-50', label: 'Moins de 50 €', min: 0, max: 50 },
  { id: '50-100', label: '50 € - 100 €', min: 50, max: 100 },
  { id: '100-500', label: '100 € - 500 €', min: 100, max: 500 },
  { id: '500-1000', label: '500 € - 1000 €', min: 500, max: 1000 },
  { id: '1000+', label: 'Plus de 1000 €', min: 1000, max: null },
];

// Types de contrat (Emploi)
const TYPES_CONTRAT = [
  { id: 'cdi', label: 'CDI' },
  { id: 'cdd', label: 'CDD' },
  { id: 'interim', label: 'Intérim' },
  { id: 'alternance', label: 'Alternance' },
  { id: 'stage', label: 'Stage' },
  { id: 'freelance', label: 'Freelance' },
];

// ══════════════════════════════════════════════════════════════════════════════
// DONNÉES FILTRES PRESTATIONS
// ══════════════════════════════════════════════════════════════════════════════

// Types de prestations par métier
const TYPES_PRESTATIONS = [
  // Plomberie
  { id: 'depannage-plomberie', label: 'Dépannage plomberie', metier: 'plomberie' },
  { id: 'fuite-eau', label: 'Réparation fuite', metier: 'plomberie' },
  { id: 'chauffe-eau', label: 'Chauffe-eau', metier: 'plomberie' },
  { id: 'debouchage', label: 'Débouchage', metier: 'plomberie' },
  { id: 'salle-de-bain', label: 'Salle de bain', metier: 'plomberie' },
  { id: 'robinetterie', label: 'Robinetterie', metier: 'plomberie' },
  // Électricité
  { id: 'tableau-electrique', label: 'Tableau électrique', metier: 'electricite' },
  { id: 'mise-aux-normes', label: 'Mise aux normes', metier: 'electricite' },
  { id: 'depannage-elec', label: 'Dépannage électrique', metier: 'electricite' },
  { id: 'eclairage', label: 'Éclairage LED', metier: 'electricite' },
  { id: 'prises', label: 'Prises / interrupteurs', metier: 'electricite' },
  { id: 'borne-recharge', label: 'Borne recharge VE', metier: 'electricite' },
  // Carrelage
  { id: 'carrelage-sol', label: 'Carrelage sol', metier: 'carrelage' },
  { id: 'carrelage-mural', label: 'Carrelage mural', metier: 'carrelage' },
  { id: 'faience', label: 'Faïence', metier: 'carrelage' },
  { id: 'terrasse', label: 'Terrasse extérieure', metier: 'carrelage' },
  // Peinture
  { id: 'peinture-interieure', label: 'Peinture intérieure', metier: 'peinture' },
  { id: 'peinture-facade', label: 'Peinture façade', metier: 'peinture' },
  { id: 'ravalement', label: 'Ravalement', metier: 'peinture' },
  { id: 'papier-peint', label: 'Papier peint', metier: 'peinture' },
  { id: 'enduit', label: 'Enduit / rebouchage', metier: 'peinture' },
  // Maçonnerie
  { id: 'construction-mur', label: 'Construction mur', metier: 'maconnerie' },
  { id: 'demolition', label: 'Démolition', metier: 'maconnerie' },
  { id: 'dalle-beton', label: 'Dalle béton', metier: 'maconnerie' },
  { id: 'ouverture-mur', label: 'Ouverture mur porteur', metier: 'maconnerie' },
  // Menuiserie
  { id: 'pose-fenetre', label: 'Pose fenêtres', metier: 'menuiserie' },
  { id: 'porte-interieure', label: 'Portes intérieures', metier: 'menuiserie' },
  { id: 'porte-entree', label: 'Porte d\'entrée', metier: 'menuiserie' },
  { id: 'placards', label: 'Placards sur mesure', metier: 'menuiserie' },
  { id: 'terrasse-bois', label: 'Terrasse bois', metier: 'menuiserie' },
  // Chauffage
  { id: 'chaudiere', label: 'Installation chaudière', metier: 'chauffage' },
  { id: 'pompe-chaleur', label: 'Pompe à chaleur', metier: 'chauffage' },
  { id: 'radiateurs', label: 'Radiateurs', metier: 'chauffage' },
  { id: 'plancher-chauffant', label: 'Plancher chauffant', metier: 'chauffage' },
  { id: 'entretien-chaudiere', label: 'Entretien chaudière', metier: 'chauffage' },
  // Couverture
  { id: 'toiture', label: 'Réfection toiture', metier: 'couverture' },
  { id: 'fuite-toiture', label: 'Réparation fuite toit', metier: 'couverture' },
  { id: 'gouttiere', label: 'Gouttières', metier: 'couverture' },
  { id: 'velux', label: 'Velux / fenêtre toit', metier: 'couverture' },
  { id: 'isolation-toiture', label: 'Isolation toiture', metier: 'couverture' },
  // Climatisation
  { id: 'installation-clim', label: 'Installation clim', metier: 'climatisation' },
  { id: 'entretien-clim', label: 'Entretien clim', metier: 'climatisation' },
  { id: 'depannage-clim', label: 'Dépannage clim', metier: 'climatisation' },
  // Serrurerie
  { id: 'ouverture-porte', label: 'Ouverture porte', metier: 'serrurerie' },
  { id: 'changement-serrure', label: 'Changement serrure', metier: 'serrurerie' },
  { id: 'blindage-porte', label: 'Blindage porte', metier: 'serrurerie' },
];

// Types de tarification pour les prestations
const TYPES_TARIFICATION = [
  { id: 'forfait', label: 'Forfait', description: 'Prix fixe tout compris', color: '#22C55E', icon: Euro },
  { id: 'horaire', label: 'À l\'heure', description: 'Tarif horaire', color: '#3B82F6', icon: Clock },
  { id: 'metre_carre', label: 'Au m²', description: 'Prix au mètre carré', color: '#06B6D4', icon: RotateCcw },
  { id: 'sur_devis', label: 'Sur devis', description: 'Devis personnalisé', color: '#8B5CF6', icon: Tag },
  { id: 'gratuit', label: 'Gratuit', description: 'Estimation gratuite', color: '#F97316', icon: CheckCircle },
];

// Options d'urgence pour les prestations
const OPTIONS_URGENCE = [
  { id: 'urgent', label: 'Intervention urgente', description: 'Disponible 24h/24 - 7j/7', color: '#EF4444', icon: Zap },
  { id: '24h', label: 'Sous 24h', description: 'Intervention rapide', color: '#F59E0B' },
  { id: '48h', label: 'Sous 48h', description: 'Délai standard', color: '#3B82F6' },
  { id: 'rdv', label: 'Sur rendez-vous', description: 'Date à convenir', color: '#6B7280' },
];

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT CONTENU FILTRE DYNAMIQUE
// ══════════════════════════════════════════════════════════════════════════════

function FilterContent({ activeFilter, filters, onFilterChange, context, rayons = [], sousRayons = [], marques = [] }) {
  const [searchTerm, setSearchTerm] = useState('');
  
  // ══════════════════════════════════════════════════════════════════════════════
  // FILTRES PRESTATIONS - Type de prestation (avec sélection métier)
  // ══════════════════════════════════════════════════════════════════════════════
  if (activeFilter === 'type_prestation') {
    // Liste des métiers
    const METIERS_LIST = [
      { id: 'plomberie', label: 'Plomberie' },
      { id: 'electricite', label: 'Électricité' },
      { id: 'peinture', label: 'Peinture' },
      { id: 'carrelage', label: 'Carrelage' },
      { id: 'maconnerie', label: 'Maçonnerie' },
      { id: 'menuiserie', label: 'Menuiserie' },
      { id: 'chauffage', label: 'Chauffage' },
      { id: 'couverture', label: 'Couverture' },
      { id: 'climatisation', label: 'Climatisation' },
      { id: 'serrurerie', label: 'Serrurerie' },
    ];
    
    const metierLabels = {
      plomberie: 'Plomberie',
      electricite: 'Électricité',
      carrelage: 'Carrelage',
      peinture: 'Peinture',
      maconnerie: 'Maçonnerie',
      menuiserie: 'Menuiserie',
      chauffage: 'Chauffage',
      couverture: 'Couverture',
      climatisation: 'Climatisation',
      serrurerie: 'Serrurerie',
    };
    
    // Filtrer les prestations par métier sélectionné
    const filteredPrestations = TYPES_PRESTATIONS.filter(p => {
      return !filters.selected_metier || p.metier === filters.selected_metier;
    });
    
    // Grouper par métier
    const groupedByMetier = filteredPrestations.reduce((acc, presta) => {
      if (!acc[presta.metier]) acc[presta.metier] = [];
      acc[presta.metier].push(presta);
      return acc;
    }, {});
    
    return (
      <div className="flex flex-col h-full">
        {/* Filtres métiers - Design cockpit */}
        <div className="p-3 border-b border-gray-100">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Métier</p>
          <div className="relative flex items-center">
            <div 
              className="flex gap-1.5 overflow-x-auto flex-1 pr-6"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>
              {/* Bouton "Tous" */}
              <button
                onClick={() => onFilterChange({ selected_metier: null })}
                className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                  !filters.selected_metier
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                Tous
              </button>
              {/* Chips de métiers */}
              {METIERS_LIST.map(metier => (
                <button
                  key={metier.id}
                  onClick={() => onFilterChange({ selected_metier: filters.selected_metier === metier.id ? null : metier.id })}
                  className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                    filters.selected_metier === metier.id
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {metier.label}
                </button>
              ))}
            </div>
            {/* Flèche orange pour indiquer le scroll */}
            <div className="flex-shrink-0 flex items-center pointer-events-none">
              <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
            </div>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-2">
          {/* Toutes les prestations (reset) */}
          <button
            onClick={() => onFilterChange({ type_prestation: null, selected_metier: null })}
            className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all mb-3 ${
              !filters.type_prestation 
                ? 'bg-[#1E40AF] text-white shadow-md' 
                : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
            }`}
          >
            Toutes les prestations
          </button>
          
          {/* Prestations groupées par métier */}
          {Object.entries(groupedByMetier).map(([metier, prestations]) => (
            <div key={metier} className="mb-3">
              <p className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {metierLabels[metier] || metier}
                <span className="text-gray-400 ml-1">({prestations.length})</span>
              </p>
              {prestations.map(presta => (
                <button 
                  key={presta.id}
                  onClick={() => onFilterChange({ 
                    type_prestation: filters.type_prestation === presta.id ? null : presta.id,
                    selected_metier: metier
                  })}
                  className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all mb-1 ${
                    filters.type_prestation === presta.id 
                      ? 'bg-[#E8650A] text-white shadow-md' 
                      : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
                  }`}
                >
                  {presta.label}
                </button>
              ))}
            </div>
          ))}
          
          {Object.keys(groupedByMetier).length === 0 && (
            <p className="text-center text-gray-400 py-4 text-sm">Aucune prestation trouvée</p>
          )}
        </div>
      </div>
    );
  }
  
  // ══════════════════════════════════════════════════════════════════════════════
  // FILTRES PRESTATIONS - Type de tarification
  // ══════════════════════════════════════════════════════════════════════════════
  if (activeFilter === 'tarification') {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-2">
          {/* Tous les types (reset) */}
          <button
            onClick={() => onFilterChange({ tarification: null })}
            className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all mb-3 ${
              !filters.tarification 
                ? 'bg-[#1E40AF] text-white shadow-md' 
                : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
            }`}
          >
            Tous les tarifs
          </button>
          
          <p className="px-2 py-1 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Type de tarification
          </p>
          
          {/* Types de tarification */}
          {TYPES_TARIFICATION.map(type => {
            const Icon = type.icon;
            const isSelected = filters.tarification === type.id;
            return (
              <button 
                key={type.id}
                onClick={() => onFilterChange({ 
                  tarification: isSelected ? null : type.id 
                })}
                className={`w-full text-left px-4 py-4 rounded-xl text-sm font-medium transition-all flex items-center gap-4 mb-2 ${
                  isSelected 
                    ? 'text-white shadow-md' 
                    : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
                }`}
                style={isSelected ? { backgroundColor: type.color } : {}}
              >
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected ? 'bg-white/20' : ''}`}
                  style={!isSelected ? { backgroundColor: `${type.color}15` } : {}}
                >
                  <Icon size={20} style={{ color: isSelected ? 'white' : type.color }} />
                </div>
                <div className="flex-1">
                  <span className="block font-semibold">{type.label}</span>
                  <span className={`text-xs ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>
                    {type.description}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  
  // ══════════════════════════════════════════════════════════════════════════════
  // FILTRES PRESTATIONS - Urgence
  // ══════════════════════════════════════════════════════════════════════════════
  if (activeFilter === 'urgence') {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-2">
          {/* Tous (reset) */}
          <button
            onClick={() => onFilterChange({ urgence: null })}
            className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all mb-3 ${
              !filters.urgence 
                ? 'bg-[#1E40AF] text-white shadow-md' 
                : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
            }`}
          >
            Tous les délais
          </button>
          
          <p className="px-2 py-1 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Délai d'intervention
          </p>
          
          {/* Options d'urgence */}
          {OPTIONS_URGENCE.map(option => {
            const Icon = option.icon || Clock;
            const isSelected = filters.urgence === option.id;
            return (
              <button 
                key={option.id}
                onClick={() => onFilterChange({ 
                  urgence: isSelected ? null : option.id 
                })}
                className={`w-full text-left px-4 py-4 rounded-xl text-sm font-medium transition-all flex items-center gap-4 mb-2 ${
                  isSelected 
                    ? 'text-white shadow-md' 
                    : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
                }`}
                style={isSelected ? { backgroundColor: option.color } : {}}
              >
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected ? 'bg-white/20' : ''}`}
                  style={!isSelected ? { backgroundColor: `${option.color}15` } : {}}
                >
                  <Icon size={20} style={{ color: isSelected ? 'white' : option.color }} />
                </div>
                <div className="flex-1">
                  <span className="block font-semibold">{option.label}</span>
                  {option.description && (
                    <span className={`text-xs ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>
                      {option.description}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  
  // RAYONS (depuis l'API - pour Marketplace et Particuliers)
  if (activeFilter === 'categorie' || activeFilter === 'rayon_filter') {
    // Utiliser les vrais rayons de l'API
    const filteredRayons = rayons.filter(r => 
      r.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    // Sous-rayons du rayon sélectionné
    const currentSousRayons = filters.rayon_id 
      ? sousRayons.filter(sr => sr.rayon_id === filters.rayon_id)
      : [];
    
    return (
      <div className="flex flex-col h-full">
        {/* Recherche */}
        <div className="p-3 border-b border-gray-100">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un rayon..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-10 pl-9 pr-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8650A] focus:ring-1 focus:ring-[#E8650A]/20"
            />
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-2">
          {/* Tous les rayons */}
          <button
            onClick={() => onFilterChange({ rayon_id: null, sous_rayon_id: null })}
            className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all mb-2 ${
              !filters.rayon_id 
                ? 'bg-[#1E40AF] text-white shadow-md' 
                : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
            }`}
          >
            Tous les rayons
          </button>
          
          <p className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">Rayons</p>
          {filteredRayons.map(rayon => (
            <div key={rayon.id}>
              <button
                onClick={() => onFilterChange({ 
                  rayon_id: filters.rayon_id === rayon.id ? null : rayon.id,
                  sous_rayon_id: null 
                })}
                className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all flex items-center gap-3 ${
                  filters.rayon_id === rayon.id 
                    ? 'bg-[#E8650A] text-white shadow-md' 
                    : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
                }`}
              >
                <span className="flex-1">{rayon.name}</span>
                {rayon.products_count !== undefined && (
                  <span className={`text-xs ${filters.rayon_id === rayon.id ? 'text-white/70' : 'text-gray-400'}`}>
                    {rayon.products_count}
                  </span>
                )}
              </button>
              
              {/* Sous-rayons si ce rayon est sélectionné */}
              {filters.rayon_id === rayon.id && currentSousRayons.length > 0 && (
                <div className="ml-4 mt-1 mb-2 space-y-1">
                  {currentSousRayons.map(sr => (
                    <button
                      key={sr.id}
                      onClick={() => onFilterChange({ sous_rayon_id: filters.sous_rayon_id === sr.id ? null : sr.id })}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                        filters.sous_rayon_id === sr.id 
                          ? 'bg-[#E8650A]/20 text-[#E8650A] font-medium' 
                          : 'hover:bg-gray-100 text-gray-600'
                      }`}
                    >
                      {sr.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          
          {filteredRayons.length === 0 && (
            <p className="text-center text-gray-400 py-4 text-sm">Aucun rayon trouvé</p>
          )}
        </div>
      </div>
    );
  }
  
  // Type de transaction (pour Marketplace) - Achat, Location, Échange, Don
  if (activeFilter === 'transaction') {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-2">
          {/* Tous les types (reset) */}
          <button
            onClick={() => onFilterChange({ transaction_type: null })}
            className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all mb-3 ${
              !filters.transaction_type 
                ? 'bg-[#1E40AF] text-white shadow-md' 
                : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
            }`}
          >
            Tous les types
          </button>
          
          {/* Types de transaction */}
          {TRANSACTION_TYPES.map(type => {
            const Icon = type.icon;
            const isSelected = filters.transaction_type === type.id;
            return (
              <button 
                key={type.id}
                onClick={() => onFilterChange({ 
                  transaction_type: isSelected ? null : type.id 
                })}
                className={`w-full text-left px-4 py-4 rounded-xl text-sm font-medium transition-all flex items-center gap-4 mb-2 ${
                  isSelected 
                    ? 'text-white shadow-md' 
                    : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
                }`}
                style={isSelected ? { backgroundColor: type.color } : {}}
              >
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected ? 'bg-white/20' : ''}`}
                  style={!isSelected ? { backgroundColor: `${type.color}15` } : {}}
                >
                  <Icon size={20} style={{ color: isSelected ? 'white' : type.color }} />
                </div>
                <div className="flex-1">
                  <span className="block font-semibold">{type.label}</span>
                  <span className={`text-xs ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>
                    {type.description}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  
  // Métiers (pour Artisans et Emploi) - Style bouton toggle comme Marketplace
  if (activeFilter === 'metier') {
    const filteredMetiers = METIERS.filter(m => 
      m.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    // Fonction pour toggle un métier (sélection simple, pas multi-select)
    const toggleMetier = (metierId) => {
      const isSelected = filters.metier === metierId;
      onFilterChange({ metier: isSelected ? null : metierId });
    };
    
    return (
      <div className="flex flex-col h-full">
        {/* Liste métiers - Boutons toggle */}
        <div className="flex-1 overflow-y-auto p-2">
          {/* Tous les métiers (reset) */}
          <button
            onClick={() => onFilterChange({ metier: null })}
            className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all mb-2 ${
              !filters.metier 
                ? 'bg-[#1E40AF] text-white shadow-md' 
                : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
            }`}
          >
            Tous les métiers
          </button>
          
          {/* Liste complète des métiers */}
          {filteredMetiers.map(metier => (
            <button 
              key={metier.id}
              onClick={() => toggleMetier(metier.id)}
              className={`w-full text-left px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-3 mb-0.5 ${
                filters.metier === metier.id 
                  ? 'bg-[#E8650A] text-white shadow-md' 
                  : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
              }`}
            >
              <span className="flex-1">{metier.label}</span>
              <span className={`text-xs ${filters.metier === metier.id ? 'text-white/70' : 'text-gray-400'}`}>
                {metier.count}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }
  
  // Localisation
  if (activeFilter === 'localisation') {
    return (
      <div className="flex flex-col h-full">
        {/* Géolocalisation */}
        <div className="p-3 border-b border-gray-100">
          <button
            onClick={() => {
              if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                  (pos) => {
                    onFilterChange({ 
                      useGeo: true,
                      lat: pos.coords.latitude,
                      lng: pos.coords.longitude 
                    });
                  },
                  () => alert('Impossible d\'accéder à ta position')
                );
              }
            }}
            className="w-full h-11 flex items-center justify-center gap-2 bg-[#E8650A]/10 text-[#E8650A] font-semibold rounded-lg hover:bg-[#E8650A]/20 transition-colors"
          >
            <MapPin size={18} />
            Utiliser ma position
          </button>
        </div>
        
        {/* Recherche ville */}
        <div className="p-3 border-b border-gray-100">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Ville ou code postal..."
              value={filters.ville || ''}
              onChange={(e) => onFilterChange({ ville: e.target.value || null })}
              className="w-full h-10 pl-9 pr-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8650A] focus:ring-1 focus:ring-[#E8650A]/20"
            />
          </div>
        </div>
        
        {/* Villes populaires */}
        <div className="flex-1 overflow-y-auto p-2">
          <p className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">Villes populaires</p>
          {VILLES_POPULAIRES.map(ville => (
            <button
              key={ville}
              onClick={() => onFilterChange({ ville })}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                filters.ville === ville 
                  ? 'bg-[#E8650A]/10 text-[#E8650A] font-medium' 
                  : 'hover:bg-gray-50 text-gray-800'
              }`}
            >
              <MapPin size={14} className="inline mr-2 opacity-50" />
              {ville}
            </button>
          ))}
        </div>
      </div>
    );
  }
  
  // Rayon
  if (activeFilter === 'rayon') {
    return (
      <div className="p-3">
        <p className="px-2 py-1 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Distance maximale
        </p>
        <div className="space-y-1">
          {RAYONS.map(rayon => (
            <button
              key={rayon.id}
              onClick={() => onFilterChange({ rayon: rayon.id })}
              className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                filters.rayon === rayon.id 
                  ? 'bg-[#E8650A] text-white shadow-md' 
                  : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
              }`}
            >
              {rayon.label}
            </button>
          ))}
        </div>
      </div>
    );
  }
  
  // Note / Avis
  if (activeFilter === 'note') {
    return (
      <div className="p-3">
        <p className="px-2 py-1 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Note minimum
        </p>
        <div className="space-y-2">
          {NOTES.map(note => (
            <button
              key={note.id}
              onClick={() => onFilterChange({ note: note.id })}
              className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all flex items-center gap-3 ${
                filters.note === note.id 
                  ? 'bg-[#E8650A] text-white shadow-md' 
                  : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
              }`}
            >
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    size={14} 
                    className={i < Math.floor(note.stars) 
                      ? (filters.note === note.id ? 'fill-white text-white' : 'fill-amber-400 text-amber-400')
                      : (filters.note === note.id ? 'text-white/40' : 'text-gray-300')
                    }
                  />
                ))}
              </div>
              <span>{note.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }
  
  // Disponibilité
  if (activeFilter === 'disponibilite') {
    return (
      <div className="p-3">
        <p className="px-2 py-1 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Quand as-tu besoin ?
        </p>
        <div className="space-y-2">
          {DISPONIBILITES.map(dispo => (
            <button
              key={dispo.id}
              onClick={() => onFilterChange({ disponibilite: dispo.id })}
              className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all flex items-center gap-3 ${
                filters.disponibilite === dispo.id 
                  ? 'bg-[#E8650A] text-white shadow-md' 
                  : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
              }`}
            >
              {dispo.icon && <dispo.icon size={16} className={filters.disponibilite === dispo.id ? 'text-white' : 'text-[#E8650A]'} />}
              {dispo.label}
            </button>
          ))}
        </div>
      </div>
    );
  }
  
  // Certifié
  if (activeFilter === 'certifie') {
    return (
      <div className="p-3">
        <p className="px-2 py-1 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Niveau de confiance
        </p>
        <div className="space-y-2">
          <button
            onClick={() => onFilterChange({ certifie: true })}
            className={`w-full text-left px-4 py-4 rounded-xl transition-all flex items-center gap-4 ${
              filters.certifie === true 
                ? 'bg-emerald-500 text-white shadow-md' 
                : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
            }`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              filters.certifie === true ? 'bg-white/20' : 'bg-emerald-100'
            }`}>
              <Shield size={20} className={filters.certifie === true ? 'text-white' : 'text-emerald-600'} />
            </div>
            <div>
              <p className="font-semibold">Pros vérifiés uniquement</p>
              <p className={`text-xs ${filters.certifie === true ? 'text-white/70' : 'text-gray-500'}`}>
                Identité et qualifications vérifiées
              </p>
            </div>
          </button>
          
          <button
            onClick={() => onFilterChange({ certifie: null })}
            className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${
              !filters.certifie 
                ? 'bg-gray-200 text-gray-700' 
                : 'bg-gray-50 hover:bg-gray-100 text-gray-600'
            }`}
          >
            Tous les artisans
          </button>
        </div>
      </div>
    );
  }
  
  // Catégorie (Particuliers / Marketplace) - Style bouton toggle comme rayons
  if (activeFilter === 'categorie') {
    const filteredCategories = CATEGORIES_PARTICULIERS.filter(c => 
      c.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    return (
      <div className="flex flex-col h-full">
        <div className="p-3 border-b border-gray-100">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une catégorie..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-10 pl-9 pr-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8650A] focus:ring-1 focus:ring-[#E8650A]/20"
            />
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-2">
          {/* Toutes les catégories (reset) */}
          <button
            onClick={() => onFilterChange({ rayon_id: null, sous_rayon_id: null })}
            className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all mb-2 ${
              !filters.rayon_id 
                ? 'bg-[#1E40AF] text-white shadow-md' 
                : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
            }`}
          >
            Toutes les catégories
          </button>
          
          <p className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">Catégories</p>
          {filteredCategories.map(cat => (
            <button 
              key={cat.id}
              onClick={() => onFilterChange({ 
                rayon_id: filters.rayon_id === cat.id ? null : cat.id,
                sous_rayon_id: null 
              })}
              className={`w-full text-left px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-3 mb-0.5 ${
                filters.rayon_id === cat.id 
                  ? 'bg-[#E8650A] text-white shadow-md' 
                  : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
              }`}
            >
              <span className="flex-1">{cat.label}</span>
              <span className={`text-xs ${filters.rayon_id === cat.id ? 'text-white/70' : 'text-gray-400'}`}>
                {cat.count}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }
  
  // Prix / Budget
  if (activeFilter === 'prix') {
    return (
      <div className="p-3">
        <p className="px-2 py-1 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Tranches de prix
        </p>
        <div className="space-y-2">
          {TRANCHES_PRIX.map(tranche => (
            <button
              key={tranche.id}
              onClick={() => onFilterChange({ prix: tranche.id, min_price: tranche.min, max_price: tranche.max })}
              className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                filters.prix === tranche.id 
                  ? 'bg-[#E8650A] text-white shadow-md' 
                  : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
              }`}
            >
              {tranche.label}
            </button>
          ))}
        </div>
        
        {/* Prix personnalisé */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="px-2 py-1 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Prix personnalisé
          </p>
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="Min €"
              value={filters.custom_min || ''}
              onChange={(e) => onFilterChange({ custom_min: e.target.value || null, prix: null })}
              className="flex-1 h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8650A]"
            />
            <input
              type="number"
              placeholder="Max €"
              value={filters.custom_max || ''}
              onChange={(e) => onFilterChange({ custom_max: e.target.value || null, prix: null })}
              className="flex-1 h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8650A]"
            />
          </div>
        </div>
      </div>
    );
  }
  
  // État du produit
  if (activeFilter === 'etat') {
    return (
      <div className="p-3">
        <p className="px-2 py-1 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          État du produit
        </p>
        <div className="space-y-2">
          {ETATS_PRODUIT.map(etat => (
            <button
              key={etat.id}
              onClick={() => onFilterChange({ etat: filters.etat === etat.id ? null : etat.id })}
              className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                filters.etat === etat.id 
                  ? 'bg-[#E8650A] text-white shadow-md' 
                  : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
              }`}
            >
              {etat.label}
            </button>
          ))}
        </div>
      </div>
    );
  }
  
  // Type de contrat (Emploi)
  if (activeFilter === 'contrat') {
    return (
      <div className="p-3">
        <p className="px-2 py-1 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Type de contrat
        </p>
        <div className="space-y-2">
          {TYPES_CONTRAT.map(contrat => (
            <button
              key={contrat.id}
              onClick={() => onFilterChange({ contrat: filters.contrat === contrat.id ? null : contrat.id })}
              className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                filters.contrat === contrat.id 
                  ? 'bg-[#2557a7] text-white shadow-md' 
                  : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
              }`}
            >
              {contrat.label}
            </button>
          ))}
        </div>
      </div>
    );
  }
  
  // Type de contenu (Réseau)
  if (activeFilter === 'type_contenu') {
    const TYPES_CONTENU = [
      { id: 'posts', label: 'Posts' },
      { id: 'chantiers', label: 'Chantiers' },
      { id: 'avant_apres', label: 'Avant / Après' },
      { id: 'conseils', label: 'Tutos & Conseils' },
      { id: 'videos', label: 'Vidéos' },
      { id: 'offres', label: 'Offres d\'emploi' },
    ];
    
    return (
      <div className="p-3">
        <p className="px-2 py-1 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Type de contenu
        </p>
        <div className="space-y-2">
          <button
            onClick={() => onFilterChange({ type_contenu: null })}
            className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${
              !filters.type_contenu 
                ? 'bg-[#1E40AF] text-white shadow-md' 
                : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
            }`}
          >
            Tous les contenus
          </button>
          {TYPES_CONTENU.map(type => (
            <button
              key={type.id}
              onClick={() => onFilterChange({ type_contenu: filters.type_contenu === type.id ? null : type.id })}
              className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                filters.type_contenu === type.id 
                  ? 'bg-[#E8650A] text-white shadow-md' 
                  : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>
    );
  }
  
  // Date / Récent (Réseau et Particuliers)
  if (activeFilter === 'date') {
    const DATES = [
      { id: 'today', label: 'Aujourd\'hui' },
      { id: 'week', label: 'Cette semaine' },
      { id: 'month', label: 'Ce mois' },
      { id: 'all', label: 'Toutes les dates' },
    ];
    
    return (
      <div className="p-3">
        <p className="px-2 py-1 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Date de publication
        </p>
        <div className="space-y-2">
          {DATES.map(date => (
            <button
              key={date.id}
              onClick={() => onFilterChange({ date: filters.date === date.id ? null : date.id })}
              className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                filters.date === date.id 
                  ? 'bg-[#E8650A] text-white shadow-md' 
                  : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
              }`}
            >
              {date.label}
            </button>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6 text-center text-gray-400">
      <SlidersHorizontal size={32} className="mx-auto mb-2 opacity-50" />
      <p className="text-sm">Sélectionne un filtre</p>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL - SYSTÈME DE FILTRES UNIFIÉ
// ══════════════════════════════════════════════════════════════════════════════

export default function UnifiedFilterSystem({
  context = 'artisans',
  isOpen,
  onClose,
  filters = {},
  onFiltersChange,
  resultsCount = 0,
  onApply,
  // Données dynamiques depuis l'API
  rayons = [],
  sousRayons = [],
  marques = [],
  // Largeur dynamique de la sidebar cockpit (override md:left-[288px])
  sidebarWidth,
}) {
  const config = FILTER_CONFIGS[context] || FILTER_CONFIGS.artisans;
  const [activeFilter, setActiveFilter] = useState(config.categories[0]?.id || 'metier');
  
  // Compter les filtres actifs
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.metiers?.length) count++;
    if (filters.ville) count++;
    if (filters.rayon && filters.rayon !== 'all') count++;
    if (filters.rayon_id) count++;
    if (filters.sous_rayon_id) count++;
    if (filters.note) count++;
    if (filters.disponibilite) count++;
    if (filters.certifie) count++;
    if (filters.prix) count++;
    if (filters.etat) count++;
    if (filters.brand) count++;
    // Filtres Prestations
    if (filters.type_prestation) count++;
    if (filters.tarification) count++;
    if (filters.urgence) count++;
    return count;
  }, [filters]);
  
  const handleFilterChange = (newFilters) => {
    onFiltersChange({ ...filters, ...newFilters });
  };
  
  const handleReset = () => {
    onFiltersChange({});
  };
  
  const handleApply = () => {
    if (onApply) onApply(filters);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay — couvre tout l'écran (y compris headers app) */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 md:left-[288px] bg-black/50 z-[9999]"
            style={sidebarWidth !== undefined ? { left: sidebarWidth } : {}}
            onClick={onClose}
          />

          {/* Panel Filtres — mobile: full-screen vrai | desktop: drawer depuis la sidebar */}
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed top-0 left-0 right-0 bottom-0 md:left-[288px] md:right-auto md:w-[440px] bg-white z-[10000] flex flex-col"
            style={sidebarWidth !== undefined ? { left: sidebarWidth } : {}}
          >
          {/* ════════════════════════════════════════════════════════════════
                HEADER — safe area top (notch iPhone / status bar Android)
            ════════════════════════════════════════════════════════════════ */}
            <div
              className="flex-shrink-0 px-4 flex items-center justify-between border-b border-gray-200 bg-white"
              style={{
                paddingTop: 'max(env(safe-area-inset-top), 16px)',
                paddingBottom: '12px',
              }}
            >
              {/* ✕ Fermer — toujours à gauche sur mobile, à droite sur desktop */}
              <button
                onClick={onClose}
                className="order-first md:order-last w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 active:scale-95 transition-all"
                aria-label="Fermer les filtres"
                data-testid="filter-close-btn"
              >
                <X size={22} className="text-gray-700" />
              </button>

              <h2 className="order-2 md:order-first font-bold text-lg text-gray-900">Filtres</h2>

              <button
                onClick={handleReset}
                className={`order-last md:order-2 text-sm font-medium transition-colors ${
                  activeFiltersCount > 0
                    ? 'text-[#E8650A] hover:text-[#D55A09]'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                disabled={activeFiltersCount === 0}
              >
                Réinitialiser
              </button>
            </div>
            
            {/* ════════════════════════════════════════════════════════════════
                CORPS - Layout 2 colonnes Amazon style
            ════════════════════════════════════════════════════════════════ */}
            <div className="flex-1 flex overflow-hidden">
              {/* Colonne gauche - Navigation filtres */}
              <div className="w-[115px] sm:w-[130px] flex-shrink-0 bg-gray-50 border-r border-gray-200 overflow-y-auto">
                {config.categories.map((cat) => {
                  const Icon = cat.icon;
                  const isActive = activeFilter === cat.id;
                  
                  // Indicateur de filtre actif pour cette catégorie
                  let hasActiveFilter = false;
                  if (cat.id === 'metier' && filters.metiers?.length) hasActiveFilter = true;
                  if (cat.id === 'localisation' && filters.ville) hasActiveFilter = true;
                  if (cat.id === 'rayon' && filters.rayon && filters.rayon !== 'all') hasActiveFilter = true;
                  if (cat.id === 'categorie' && filters.rayon_id) hasActiveFilter = true;
                  if (cat.id === 'note' && filters.note) hasActiveFilter = true;
                  if (cat.id === 'disponibilite' && filters.disponibilite) hasActiveFilter = true;
                  if (cat.id === 'certifie' && filters.certifie) hasActiveFilter = true;
                  if (cat.id === 'prix' && (filters.prix || filters.min_price || filters.max_price)) hasActiveFilter = true;
                  if (cat.id === 'etat' && filters.etat) hasActiveFilter = true;
                  if (cat.id === 'contrat' && filters.contrat) hasActiveFilter = true;
                  if (cat.id === 'marque' && filters.brand) hasActiveFilter = true;
                  // Indicateurs pour filtres Prestations
                  if (cat.id === 'type_prestation' && filters.type_prestation) hasActiveFilter = true;
                  if (cat.id === 'tarification' && filters.tarification) hasActiveFilter = true;
                  if (cat.id === 'urgence' && filters.urgence) hasActiveFilter = true;
                  
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setActiveFilter(cat.id)}
                      className={`w-full text-left px-3 py-3 flex flex-col items-center gap-1 relative transition-all ${
                        isActive 
                          ? 'bg-white text-[#E8650A]' 
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {/* Indicateur gauche actif */}
                      {isActive && (
                        <div className="absolute left-0 top-2 bottom-2 w-1 bg-[#E8650A] rounded-r-full" />
                      )}
                      
                      {/* Badge filtre actif */}
                      {hasActiveFilter && (
                        <div className="absolute top-2 right-2 w-2 h-2 bg-[#E8650A] rounded-full" />
                      )}
                      
                      <Icon size={20} className={isActive ? 'text-[#E8650A]' : 'text-gray-400'} />
                      <span className={`text-xs text-center leading-tight ${isActive ? 'font-semibold' : 'font-medium'}`}>
                        {cat.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              
              {/* Colonne droite - Contenu dynamique */}
              <div className="flex-1 overflow-y-auto bg-white">
                <FilterContent
                  activeFilter={activeFilter}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  context={context}
                  rayons={rayons}
                  sousRayons={sousRayons}
                  marques={marques}
                />
              </div>
            </div>
            
            {/* ════════════════════════════════════════════════════════════════
                FOOTER - CTA Sticky (avec padding pour la nav mobile)
            ════════════════════════════════════════════════════════════════ */}
            <div
              className="flex-shrink-0 px-4 pt-3 md:pb-4 border-t border-gray-200 bg-white"
              style={{ paddingBottom: 'max(calc(env(safe-area-inset-bottom) + 12px), 16px)' }}
            >
              <button
                onClick={handleApply}
                className="w-full h-14 bg-[#E8650A] hover:bg-[#D55A09] text-white font-bold text-base rounded-xl shadow-lg shadow-[#E8650A]/25 transition-all active:scale-[0.98]"
              >
                Voir {resultsCount} résultat{resultsCount > 1 ? 's' : ''}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT CHIPS FILTRES RAPIDES
// ══════════════════════════════════════════════════════════════════════════════

export function QuickFilterChips({ 
  context = 'artisans', 
  filters = {}, 
  onOpenFilter,
  onClearFilter 
}) {
  const config = FILTER_CONFIGS[context] || FILTER_CONFIGS.artisans;
  
  // Labels pour les filtres actifs
  const getFilterLabel = (filterId) => {
    if (filterId === 'metier' && filters.metiers?.length) {
      if (filters.metiers.length === 1) {
        return METIERS.find(m => m.id === filters.metiers[0])?.label || 'Métier';
      }
      return `${filters.metiers.length} métiers`;
    }
    if (filterId === 'localisation' && filters.ville) {
      return filters.ville;
    }
    if (filterId === 'rayon' && filters.rayon && filters.rayon !== 'all') {
      return RAYONS.find(r => r.id === filters.rayon)?.label || 'Rayon';
    }
    if (filterId === 'note' && filters.note) {
      return `${filters.note}★ et +`;
    }
    if (filterId === 'disponibilite' && filters.disponibilite) {
      return DISPONIBILITES.find(d => d.id === filters.disponibilite)?.label || 'Dispo';
    }
    if (filterId === 'certifie' && filters.certifie) {
      return 'Certifié';
    }
    return null;
  };
  
  const isFilterActive = (filterId) => {
    if (filterId === 'metier') return filters.metiers?.length > 0;
    if (filterId === 'localisation') return !!filters.ville;
    if (filterId === 'rayon') return filters.rayon && filters.rayon !== 'all';
    if (filterId === 'note') return !!filters.note;
    if (filterId === 'disponibilite') return !!filters.disponibilite;
    if (filterId === 'certifie') return !!filters.certifie;
    return false;
  };
  
  return (
    <div 
      className="flex gap-2 overflow-x-auto py-3 px-4 scrollbar-hide"
      style={{ 
        scrollbarWidth: 'none',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
      `}</style>
      
      {config.quickFilters.map((filterId) => {
        const cat = config.categories.find(c => c.id === filterId);
        if (!cat) return null;
        
        const Icon = cat.icon;
        const isActive = isFilterActive(filterId);
        const label = getFilterLabel(filterId) || cat.label;
        
        return (
          <button
            key={filterId}
            onClick={() => onOpenFilter(filterId)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              isActive
                ? 'bg-[#E8650A] text-white shadow-md'
                : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300 shadow-sm'
            }`}
          >
            <Icon size={16} />
            <span>{label}</span>
            {isActive && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClearFilter(filterId);
                }}
                className="ml-1 w-5 h-5 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30"
              >
                <X size={12} />
              </button>
            )}
          </button>
        );
      })}
      
      {/* Bouton Tous les filtres */}
      <button
        onClick={() => onOpenFilter(null)}
        className="flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
      >
        <Filter size={16} />
        Tous les filtres
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT CTA FILTRE - ICÔNE SLIDERS + TEXTE CONTEXTUEL
// Design bleu uniforme mobile/tablette/desktop
// ══════════════════════════════════════════════════════════════════════════════

const CONTEXT_PHRASES = {
  artisans: 'Trouve ton artisan en 3 clics',
  prestations: 'Trouve ta prestation en 3 clics',
  produits_pro: 'Trouve ton produit pro en 3 clics',
  marketplace: 'Trouve ton produit en 3 clics',
  produits: 'Trouve ton produit en 3 clics',
  particuliers: 'Trouve ton annonce en 3 clics',
  emploi: 'Trouve ton emploi en 3 clics',
  reseau: 'Trouve le bon contenu en 3 clics'
};

export function FilterButton({ onClick, activeCount = 0, context = 'artisans', className = '' }) {
  const [hasAnimated, setHasAnimated] = useState(false);
  
  useEffect(() => {
    // Animation une seule fois par session
    const sessionKey = 'filter_btn_animated';
    if (!sessionStorage.getItem(sessionKey)) {
      const timer = setTimeout(() => {
        setHasAnimated(true);
        sessionStorage.setItem(sessionKey, 'true');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Couleurs bleu navbar (#1E3A5F)
  const blueLight = '#E8F4FD';
  const bluePrimary = '#1E3A5F';
  const blueText = '#0F172A';
  
  const phrase = CONTEXT_PHRASES[context] || CONTEXT_PHRASES.artisans;

  return (
    <motion.button
      onClick={onClick}
      data-testid="btn-filtrer"
      className={`inline-flex items-center gap-3 px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl text-sm sm:text-base font-semibold transition-all ${className}`}
      style={{
        background: blueLight,
        border: `2px solid ${bluePrimary}`,
        color: blueText,
        boxShadow: '0 2px 8px rgba(30, 58, 95, 0.12)'
      }}
      initial={{ x: 30, opacity: 0 }}
      animate={{ 
        x: 0, 
        opacity: 1,
        boxShadow: hasAnimated 
          ? '0 2px 8px rgba(30, 58, 95, 0.12)' 
          : '0 0 0 4px rgba(30, 58, 95, 0.15), 0 2px 8px rgba(30, 58, 95, 0.12)'
      }}
      transition={{ 
        x: { duration: 0.35, ease: 'easeOut', delay: 0.8 },
        opacity: { duration: 0.35, delay: 0.8 },
        boxShadow: { duration: 1.5, delay: 1.2 }
      }}
      whileHover={{ scale: 1.02, boxShadow: '0 4px 16px rgba(30, 58, 95, 0.2)' }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Icône Sliders - 3 lignes avec ronds */}
      <SlidersHorizontal size={20} className="text-[#1E3A5F] flex-shrink-0" strokeWidth={2.5} />
      
      {/* Texte contextuel */}
      <span className="whitespace-nowrap">{phrase}</span>
      
      {/* Badge compteur si filtres actifs */}
      {activeCount > 0 && (
        <span 
          className="min-w-[24px] h-[24px] flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0"
          style={{
            background: bluePrimary,
            color: 'white'
          }}
        >
          {activeCount}
        </span>
      )}
    </motion.button>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PHRASE CONTEXTUELLE (LEGACY - pour rétrocompatibilité)
// ══════════════════════════════════════════════════════════════════════════════

export function FilterContextPhrase({ context = 'artisans' }) {
  // Ce composant n'est plus nécessaire car le texte est intégré au bouton
  // Gardé pour rétrocompatibilité mais ne rend rien
  return null;
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT BADGES FILTRES ACTIFS
// ══════════════════════════════════════════════════════════════════════════════

export function ActiveFilterBadges({ filters = {}, onClearFilter, onClearAll }) {
  const badges = [];
  
  if (filters.metiers?.length) {
    filters.metiers.forEach(m => {
      const metier = METIERS.find(mt => mt.id === m);
      if (metier) {
        badges.push({ key: `metier-${m}`, label: metier.label, type: 'metier', value: m });
      }
    });
  }
  
  if (filters.ville) {
    badges.push({ key: 'ville', label: filters.ville, type: 'ville' });
  }
  
  if (filters.rayon && filters.rayon !== 'all') {
    const rayon = RAYONS.find(r => r.id === filters.rayon);
    if (rayon) {
      badges.push({ key: 'rayon', label: rayon.label, type: 'rayon' });
    }
  }
  
  if (filters.note) {
    badges.push({ key: 'note', label: `${filters.note}★ et +`, type: 'note' });
  }
  
  if (filters.disponibilite) {
    const dispo = DISPONIBILITES.find(d => d.id === filters.disponibilite);
    if (dispo) {
      badges.push({ key: 'disponibilite', label: dispo.label, type: 'disponibilite' });
    }
  }
  
  if (filters.certifie) {
    badges.push({ key: 'certifie', label: 'Certifié', type: 'certifie' });
  }
  
  if (badges.length === 0) return null;
  
  return (
    <div className="flex items-center gap-2 px-4 py-2 overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
      <style>{`.scrollbar-hide::-webkit-scrollbar { display: none; }`}</style>
      
      {badges.map(badge => (
        <span
          key={badge.key}
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium whitespace-nowrap"
        >
          {badge.label}
          <button
            onClick={() => onClearFilter(badge.type, badge.value)}
            className="hover:bg-gray-200 rounded p-0.5 ml-0.5"
          >
            <X size={10} />
          </button>
        </span>
      ))}
      
      <button
        onClick={onClearAll}
        className="text-xs text-gray-500 hover:text-red-600 font-medium whitespace-nowrap ml-1"
      >
        Tout effacer
      </button>
    </div>
  );
}
